/* Why EZT: one shared product drawer; photos load only when it opens. */
(() => {
  'use strict';
  if (window.EZTWhy) { window.EZTWhy.init(); return; }
  const controllers = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  function metric(name, dialog, extra = {}) {
    try {
      if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ezt_product_id: dialog.dataset.eztWhyProductId || '', ...extra });
    } catch (_) {}
  }
  class WhyPanel {
    constructor(dialog) {
      this.dialog = dialog;
      this.slideshow = dialog.querySelector('[data-ezt-why-slideshow]');
      this.pool = Array.from(dialog.querySelectorAll('[data-ezt-why-slide]'));
      this.slides = this.selectSlides();
      this.caption = dialog.querySelector('[data-ezt-why-caption]');
      this.captionText = dialog.querySelector('[data-ezt-why-caption-text]');
      this.count = dialog.querySelector('[data-ezt-why-count]');
      this.pauseButton = dialog.querySelector('[data-ezt-why-pause]');
      this.autoplay = this.slideshow?.dataset.autoplay === 'true';
      this.interval = Math.max(3, Math.min(12, Number(this.slideshow?.dataset.interval) || 3)) * 1000;
      this.index = 0;
      this.paused = !this.autoplay || reducedMotion.matches;
      this.bind();
      this.update(false);
    }
    selectSlides() {
      if (!this.slideshow || !this.pool.length) return [];
      let indices = this.pool.map((_, index) => index);
      if (this.slideshow.dataset.randomize === 'true' && indices.length > 3) {
        const signature = this.pool.map(slide => slide.querySelector('img').dataset.eztWhySrc).join('|');
        let hash = 2166136261;
        for (let i = 0; i < signature.length; i++) hash = Math.imul(hash ^ signature.charCodeAt(i), 16777619);
        const key = `ezt-why-photos-v1:${hash >>> 0}`;
        let saved;
        try { saved = JSON.parse(sessionStorage.getItem(key)); } catch (_) {}
        if (Array.isArray(saved) && saved.length === 3 && new Set(saved).size === 3 && saved.every(i => Number.isInteger(i) && i >= 0 && i < this.pool.length)) indices = saved;
        else {
          for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
          }
          indices = indices.slice(0, 3);
          try { sessionStorage.setItem(key, JSON.stringify(indices)); } catch (_) {}
        }
      }
      this.pool.forEach((slide, index) => { slide.hidden = !indices.includes(index); slide.classList.remove('is-active', 'is-previous'); });
      const slides = indices.map(index => this.pool[index]);
      slides.forEach((slide, index) => slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`));
      return slides;
    }
    bind() {
      this.dialog.querySelector('[data-ezt-why-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.stop();
        this.photoRun = (this.photoRun || 0) + 1;
        if (!this.hadHtmlLock) document.documentElement.classList.remove('ezt-why-scroll-lock');
        if (!this.hadBodyLock) document.body.classList.remove('ezt-why-scroll-lock');
        document.querySelectorAll('[data-ezt-why-open]').forEach(trigger => {
          if (trigger.dataset.eztWhyOpen === this.dialog.id) trigger.setAttribute('aria-expanded', 'false');
        });
        if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      });
      this.dialog.querySelector('[data-ezt-why-previous]')?.addEventListener('click', () => this.advance(-1, true));
      this.dialog.querySelector('[data-ezt-why-next]')?.addEventListener('click', () => this.advance(1, true));
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        if (!this.paused) { this.hovered = false; this.focusWithin = false; }
        this.updatePause(); this.schedule();
      });
      this.slideshow?.addEventListener('mouseenter', () => { this.hovered = true; this.stop(); });
      this.slideshow?.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); });
      this.slideshow?.addEventListener('focusin', () => { this.focusWithin = true; this.stop(); });
      this.slideshow?.addEventListener('focusout', event => {
        this.focusWithin = this.slideshow.contains(event.relatedTarget);
        this.schedule();
      });
      this.slideshow?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' || event.target.closest('button')) return;
        this.swipe = { x: event.clientX, y: event.clientY };
      });
      this.slideshow?.addEventListener('pointerup', event => {
        if (!this.swipe) return;
        const dx = event.clientX - this.swipe.x, dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.advance(dx < 0 ? 1 : -1, true);
      });
      this.slideshow?.addEventListener('pointercancel', () => { this.swipe = null; });
      this.slideshow?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault(); this.advance(event.key === 'ArrowRight' ? 1 : -1, true);
      });
      this.dialog.querySelectorAll('[data-ezt-why-action]').forEach(link => link.addEventListener('click', event => {
        metric('ezt_why_action', this.dialog, { ezt_action: link.dataset.eztWhyAction });
        if (link.dataset.eztWhyAction !== 'ask' || !window.EZTSupport) return;
        const ask = this.trigger?.closest('.ezt-help-actions')?.querySelector('[data-ezt-question-open]') || document.querySelector('[data-ezt-question-open]');
        const target = ask && document.getElementById(ask.dataset.eztQuestionOpen);
        if (!target || typeof target.showModal !== 'function') return;
        event.preventDefault();
        // Support owns its own scroll lock; finish closing this modal first.
        this.dialog.addEventListener('close', () => { window.EZTSupport.init(); ask.click(); }, { once: true });
        this.dialog.close();
      }));
      this.visibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.visibility);
      this.motion = () => {
        if (reducedMotion.matches) this.paused = true;
        this.updatePause(); this.schedule();
      };
      reducedMotion.addEventListener?.('change', this.motion);
    }
    open(trigger) {
      if (this.dialog.open || typeof this.dialog.showModal !== 'function') return false;
      this.trigger = trigger;
      this.hadHtmlLock = document.documentElement.classList.contains('ezt-why-scroll-lock');
      this.hadBodyLock = document.body.classList.contains('ezt-why-scroll-lock');
      this.hydrate(this.index);
      this.dialog.showModal();
      document.documentElement.classList.add('ezt-why-scroll-lock');
      document.body.classList.add('ezt-why-scroll-lock');
      trigger.setAttribute('aria-expanded', 'true');
      this.dialog.querySelector('.ezt-why__scroll').scrollTop = 0;
      this.hovered = false; this.focusWithin = false;
      this.update(false); this.schedule();
      requestAnimationFrame(() => this.dialog.querySelector('[data-ezt-why-close]').focus({ preventScroll: true }));
      metric('ezt_why_open', this.dialog);
      return true;
    }
    hydrate(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image) return Promise.resolve();
      if (!image.hasAttribute('src')) {
        if (image.dataset.eztWhySrcset) image.srcset = image.dataset.eztWhySrcset;
        image.src = image.dataset.eztWhySrc;
      }
      return image.decode ? image.decode().catch(() => {}) : Promise.resolve();
    }
    update(manual) {
      if (!this.slides.length) return;
      clearTimeout(this.fadeTimer);
      const previous = this.slides.find(slide => slide.classList.contains('is-active'));
      const next = this.slides[this.index];
      this.slides.forEach((slide, index) => {
        slide.classList.remove('is-previous');
        slide.classList.toggle('is-active', index === this.index);
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      if (previous && previous !== next && !reducedMotion.matches) {
        previous.classList.add('is-previous');
        this.fadeTimer = setTimeout(() => previous.classList.remove('is-previous'), 700);
      }
      if (this.dialog.open) {
        this.hydrate(this.index);
        this.hydrate((this.index + 1) % this.slides.length);
      }
      const caption = this.slides[this.index]?.dataset.caption || '';
      this.captionText.textContent = caption;
      this.caption.hidden = !caption;
      this.count.setAttribute('aria-live', manual ? 'polite' : 'off');
      this.count.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.dialog.querySelector('.ezt-why__controls').hidden = this.slides.length < 2;
      this.updatePause();
    }
    async advance(direction, manual) {
      if (this.slides.length < 2) return;
      if (manual) this.paused = true;
      const nextIndex = (this.index + direction + this.slides.length) % this.slides.length;
      const run = this.photoRun = (this.photoRun || 0) + 1;
      this.stop();
      await this.hydrate(nextIndex);
      if (run !== this.photoRun || !this.dialog.open) return;
      this.index = nextIndex;
      this.update(manual); this.schedule();
      if (manual) metric('ezt_why_slide', this.dialog, { ezt_slide: this.index + 1 });
    }
    updatePause() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.querySelector('[data-ezt-why-pause-icon]').hidden = this.paused;
      this.pauseButton.querySelector('[data-ezt-why-play-icon]').hidden = !this.paused;
    }
    stop() { clearTimeout(this.timer); }
    schedule() {
      this.stop();
      if (!this.dialog.open || document.hidden || this.paused || this.hovered || this.focusWithin || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.advance(1, false), this.interval);
    }
    destroy() {
      if (this.dialog.open) this.dialog.close();
      this.stop();
      document.removeEventListener('visibilitychange', this.visibility);
      reducedMotion.removeEventListener?.('change', this.motion);
    }
  }
  function init() {
    controllers.forEach((controller, id) => {
      if (!controller.dialog.isConnected) { controller.destroy(); controllers.delete(id); }
    });
    document.querySelectorAll('[data-ezt-why-dialog]').forEach(dialog => {
      if (!controllers.has(dialog.id)) controllers.set(dialog.id, new WhyPanel(dialog));
    });
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-ezt-why-open]');
    if (!trigger || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    init();
    const controller = controllers.get(trigger.dataset.eztWhyOpen);
    if (controller?.open(trigger)) event.preventDefault();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  window.EZTWhy = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
